import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Spin, Space, Typography, theme } from 'antd';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import type { NocSimConnectResult } from '@goelectric/shared-types';
import { ApiError } from '@/lib/http/apiClient';
import { simApi } from '../api/simApi';

interface ConnectPanelProps {
  defaultStationId?: string | null;
  /** Se invoca al terminar la prueba para refrescar el registro de cargadores. */
  onConnected?: () => void;
}

type StepState = 'pending' | 'active' | 'ok' | 'fail';

/**
 * Ritmo visual del proceso. La API es UNA sola llamada: no hay eventos
 * intermedios, así que los pasos avanzan con temporizador mientras se espera y
 * se resuelven con el resultado real. Si el core responde antes, se cortan.
 */
const STEP_PACING_MS = [500, 1200] as const;

/** Panel de prueba de alta: abre una conexión efímera y envía BootNotification al core. */
export function ConnectPanel({ defaultStationId, onConnected }: ConnectPanelProps) {
  const [stationId, setStationId] = useState(defaultStationId ?? '');
  const previousDefault = useRef(defaultStationId ?? '');
  const [connecting, setConnecting] = useState(false);
  const [phase, setPhase] = useState(0);
  const [result, setResult] = useState<NocSimConnectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Sincroniza con la estación seleccionada sin pisar lo que escribió el usuario:
  // solo rellena si el campo está vacío o si cambió la estación seleccionada.
  useEffect(() => {
    const next = defaultStationId ?? '';
    setStationId((current) => (!current || current === previousDefault.current ? next : current));
    previousDefault.current = next;
  }, [defaultStationId]);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  useEffect(() => clearTimers, []);

  const connect = async () => {
    const id = stationId.trim();
    if (!id) {
      return;
    }
    clearTimers();
    setConnecting(true);
    setPhase(0);
    setError(null);
    setResult(null);
    STEP_PACING_MS.forEach((delay, index) => {
      timers.current.push(setTimeout(() => setPhase(index + 1), delay));
    });
    try {
      setResult(await simApi.connect({ stationId: id }));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'No se pudo conectar con el core.');
    } finally {
      clearTimers();
      setConnecting(false);
      onConnected?.();
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Typography.Text>ChargePoint ID</Typography.Text>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={stationId}
            onChange={(event) => setStationId(event.target.value)}
            placeholder="Ej. CP-001"
            aria-label="ChargePoint ID del cargador a conectar"
            onPressEnter={() => void connect()}
            disabled={connecting}
          />
          <Button type="primary" loading={connecting} disabled={!stationId.trim()} onClick={() => void connect()}>
            Conectar
          </Button>
        </Space.Compact>
      </Space>

      {connecting && <ConnectProgress phase={phase} />}

      {error && <Alert type="error" showIcon message={error} />}

      {result && <ConnectResultView result={result} />}

      <Link to="/cargadores/nuevo">Dar de alta un cargador →</Link>
    </Space>
  );
}

/** Proceso en curso: el paso activo gira, los anteriores se dan por hechos. */
function ConnectProgress({ phase }: { phase: number }) {
  return (
    <StepList
      rows={[
        { state: phase >= 1 ? 'ok' : 'active', label: 'Resolviendo tenant y protocolo del cargador' },
        { state: phase >= 2 ? 'ok' : phase === 1 ? 'active' : 'pending', label: 'Abriendo el socket contra el core' },
        { state: phase >= 2 ? 'active' : 'pending', label: 'Enviando BootNotification' },
      ]}
    />
  );
}

/**
 * Deriva los pasos del veredicto a partir del `outcome`, NO de `targetUrl`: un
 * `unavailable` puede traer la URL del endpoint aunque el upgrade nunca se haya
 * intentado. `targetUrl` solo se usa como texto cuando el socket sí abrió.
 *
 * Pura y exportada para poder testear la tabla de veredictos sin renderizar.
 */
export function connectResultSteps(result: NocSimConnectResult): StepRow[] {
  const { outcome } = result;

  // `unavailable` = el socket no abrió. Un `rejected` sin targetUrl tampoco
  // llegó al upgrade (p. ej. el aliado no tiene tenant sincronizado).
  const socketOpened = outcome !== 'unavailable' && !(outcome === 'rejected' && !result.targetUrl);

  const socket: StepRow = socketOpened
    ? {
        state: 'ok',
        label: result.targetUrl ? (
          <>
            Socket <Typography.Text code>{result.targetUrl}</Typography.Text>
          </>
        ) : (
          'Socket abierto'
        ),
      }
    : {
        state: 'fail',
        label: result.targetUrl ? (
          <>
            Socket <Typography.Text code>{result.targetUrl}</Typography.Text> (no se abrió)
          </>
        ) : (
          'No se pudo abrir el socket'
        ),
      };

  let boot: StepRow;
  if (!socketOpened) {
    boot = {
      state: 'pending',
      label: 'BootNotification no enviado: el socket no se abrió',
    };
  } else if (outcome === 'accepted') {
    boot = {
      state: 'ok',
      label: `BootNotification → ${result.bootStatus ?? 'Accepted'}${
        result.interval ? ` (heartbeat ${result.interval} s)` : ''
      }`,
    };
  } else if (outcome === 'timeout') {
    boot = { state: 'fail', label: 'Sin respuesta del core en el plazo' };
  } else if (!result.registered) {
    boot = { state: 'fail', label: `Cargador no registrado previamente («${result.stationId}»)` };
  } else {
    boot = {
      state: 'fail',
      label: `BootNotification → ${result.bootStatus ?? 'rechazado'}`,
      detail: result.detail,
    };
  }

  return [
    { state: 'ok', label: `Tenant ${result.tenantId} · protocolo ${result.protocol}` },
    socket,
    boot,
  ];
}

const FAILURE_TITLE: Record<NocSimConnectResult['outcome'], string> = {
  accepted: 'Cargador sincronizado',
  rejected: 'El core rechazó el alta',
  timeout: 'El core no respondió en el plazo',
  unavailable: 'No se pudo abrir la conexión con el core',
};

function ConnectResultView({ result }: { result: NocSimConnectResult }) {
  const accepted = result.outcome === 'accepted';
  // El CP_ID fuera del registro no es un fallo de red: el paso lo dice y no se
  // repite el detalle en el veredicto de error.
  const hasVerdict = !accepted && !(result.outcome === 'rejected' && !result.registered);

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <StepList rows={connectResultSteps(result)} />

      {accepted && (
        <Alert
          type="success"
          showIcon
          message="Cargador sincronizado"
          description={`Tenant ${result.tenantId} · ${result.elapsedMs} ms`}
        />
      )}

      {hasVerdict && (
        <Alert
          type="error"
          showIcon
          message={FAILURE_TITLE[result.outcome]}
          description={result.detail}
        />
      )}
    </Space>
  );
}

interface StepRow {
  state: StepState;
  label: React.ReactNode;
  detail?: string;
}

function StepList({ rows }: { rows: StepRow[] }) {
  return (
    <Space direction="vertical" size={6} style={{ width: '100%' }}>
      {rows.map((row, index) => (
        <StepItem key={index} {...row} />
      ))}
    </Space>
  );
}

function StepItem({ state, label, detail }: StepRow) {
  const { token } = theme.useToken();
  return (
    <Space align="start" size={8}>
      <span style={{ display: 'inline-flex', width: 14, height: 14, marginTop: 3 }}>
        <StepIcon state={state} success={token.colorSuccess} error={token.colorError} />
      </span>
      <Space direction="vertical" size={0}>
        <Typography.Text type={state === 'pending' ? 'secondary' : undefined}>{label}</Typography.Text>
        {detail && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {detail}
          </Typography.Text>
        )}
      </Space>
    </Space>
  );
}

function StepIcon({ state, success, error }: { state: StepState; success: string; error: string }) {
  if (state === 'active') {
    return <Spin size="small" />;
  }
  if (state === 'ok') {
    return <CheckCircleFilled style={{ color: success }} />;
  }
  if (state === 'fail') {
    return <CloseCircleFilled style={{ color: error }} />;
  }
  return (
    <span
      style={{
        width: 12,
        height: 12,
        marginTop: 1,
        borderRadius: '50%',
        border: '1px solid currentColor',
        opacity: 0.35,
      }}
    />
  );
}
